import React, {useState, useEffect} from 'react';
import {VStack} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {useContextState} from '../../../../../context/sub-client';
import {useContextState as useUserContext} from '../../../../../context/user';
import InternalTemplate from '../../../../../components/templates/internal';
import {StackParamList} from '../../../../../navigators';
import {message as t} from '../../../../../utils/messages';
import {SpinnerModal} from '../../../../../components/modals';
import {PaymentForm} from '../../../commons/proof-payment/forms';
import {IPaymentForm} from '../../../../../interfaces/forms';
import useToast from '../../../../../hooks';
import {ResultOfRequestCached} from '../../../../../interfaces/types/result-of-request-cached';
import {formatFeeQuote} from '../../../../../utils/format';
import { readFile } from './utils/read-file';

type ScreenProps = NativeStackScreenProps<
  StackParamList,
  'FeePaymentProofScreen'
>;

const FeePaymentProofScreen = ({navigation}: ScreenProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showRemoteError, setShowRemoteError] = useState(false);
  const {remoteMessageError, setRemoteMessageError} = useUserContext();
  const {invoiceFeeSummary, sendProofFeePayment} = useContextState();

  const {showRemoteMessageError, showProofError, showReadFileError} =
    useToast();

  const launchPaymentProofError = () => {
    if (remoteMessageError) {
      showRemoteMessageError(remoteMessageError);
      setRemoteMessageError(null);
    } else {
      showProofError();
    }
  };

  useEffect(() => {
    if (showRemoteError) {
      launchPaymentProofError();
      setShowRemoteError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRemoteError]);

  const handleGoBack = () => {
    navigation.pop(1);
  };

  const getFeeList = () => {
    if (
      invoiceFeeSummary &&
      invoiceFeeSummary.fees &&
      invoiceFeeSummary.fees.length > 0
    ) {
      return invoiceFeeSummary.fees.map(fee => fee.id);
    }
    return [];
  };

  const getFeeListInputValue = () => {
    if (
      invoiceFeeSummary &&
      invoiceFeeSummary.fees &&
      invoiceFeeSummary.fees.length > 0
    ) {
      return invoiceFeeSummary.fees
        .map(fee => formatFeeQuote(fee.dueDate.day, fee.dueDate.month))
        .join(', ');
    }
    return '';
  };

  const submitUserPaymentProof = async (
    proof: IPaymentForm,
  ): Promise<boolean> => {
    try {
      setIsLoading(true);
      if (await readFile(proof)) {
        return await sendProofFeePayment({
          ...proof,
          invoice: invoiceFeeSummary?.id,
          fees: getFeeList(),
        });
      }
    } catch {
      setIsLoading(false);
      showReadFileError();
    }
    return false;
  };

  const handleEndSubmitProof = (result: boolean | ResultOfRequestCached) => {
    setIsLoading(false);
    if (result === true) {
      navigation.navigate('FeePaymentProofSuccessScreen');
    } else if (result === false) {
      setShowRemoteError(true);
    }
  };

  return (
    <InternalTemplate
      title={'screen-fee-payment-proof-title'}
      contentPaddingX="0"
      backButton={{
        onPress: () => handleGoBack(),
        label: 'back-dashboard',
        usage: 'external',
      }}>
      <VStack space={3} justifyContent="center" marginX={4} marginBottom={200}>
        <SpinnerModal
          show={isLoading}
          title={t('screen-payment-voucher-send-loading-title')}
        />
        <PaymentForm
          onSubmit={submitUserPaymentProof}
          onEndSubmit={handleEndSubmitProof}
          invoice={{
            code: `${invoiceFeeSummary?.id}`,
            fees: getFeeListInputValue(),
          }}
          primaryButtonLabel="screen-payment-voucher-primary-button"
        />
      </VStack>
    </InternalTemplate>
  );
};
export {FeePaymentProofScreen};
